import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { paymentGatewayApi } from "../API/paymentGatewayApi";
import { paymentGatewayConfigurationApi } from "../API/paymentGatewayConfigurationApi";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import Menu from "../Component/Menu";
import TextfieldComponent from "../Component/TextfieldComponent";
import Breadcrumb from "../NewUI/NewComponents/Breadcrumb";
import SelectField from "../NewUI/NewComponents/SelectField";
import Swal from "sweetalert2";
import GatewayLogo from "./GatewayLogo";

const secretFields = [
  { name: "merchantId", label: "Merchant ID" },
  { name: "clientId", label: "Client ID" },
  { name: "clientSecret", label: "Client Secret" },
  { name: "apiKey", label: "API Key" },
  { name: "secretKey", label: "Secret Key" },
  { name: "webhookSecret", label: "Webhook Secret" },
  { name: "accessToken", label: "Access Token" },
  { name: "refreshToken", label: "Refresh Token" },
];

export default function ConfigurationForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(id);
  const [gateways, setGateways] = useState([]);
  const [form, setForm] = useState({
    gatewayId: "",
    tenantId: "",
    merchantId: "",
    clientId: "",
    clientSecret: "",
    apiKey: "",
    secretKey: "",
    webhookSecret: "",
    accessToken: "",
    refreshToken: "",
    production: false,
    active: true,
  });

  const loadGateways = async (selectId) => {
    try {
      const res = await paymentGatewayApi.getAll();
      const list = res.data?.data || res.data || [];
      const ordered = [...list].sort((a, b) => (a.priority ?? 9999) - (b.priority ?? 9999));
      setGateways(ordered);
      if (selectId && list.some((g) => String(g.id) === String(selectId))) {
        setForm((p) => ({ ...p, gatewayId: selectId }));
      }
    } catch {}
  };

  useEffect(() => {
    // Resume flow: came back from /gateways/create with newGatewayId + draft
    const incoming = location.state;
    if (incoming?.draft && !isEdit) {
      setForm((p) => ({ ...p, ...incoming.draft, gatewayId: incoming.newGatewayId || incoming.draft.gatewayId || "" }));
    }
    loadGateways(incoming?.newGatewayId);
    if (isEdit) {
      paymentGatewayConfigurationApi.getById(id).then((res) => {
        const d = res.data || {};
        setForm((p) => ({ ...p, ...d, gatewayId: d.gateway?.id || d.gatewayId || "" }));
      }).catch(() => Swal.fire("Failed to load configuration", "", "error"));
    }
  }, [id]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Default to top-priority active gateway on create
  useEffect(() => {
    if (!isEdit && !form.gatewayId && gateways.length > 0) {
      const top = gateways.find((g) => g.active !== false) || gateways[0];
      if (top) setForm((p) => (p.gatewayId ? p : { ...p, gatewayId: top.id }));
    }
  }, [gateways]);

  const goAddGateway = () => {
    navigate("/gateways/create", { state: { returnTo: "/gateway-configurations/create", draft: form } });
  };

  const selectedGateway = gateways.find((g) => String(g.id) === String(form.gatewayId));

  const submit = async () => {
    if (!form.gatewayId || !form.tenantId?.trim()) {
      Swal.fire("Gateway and Tenant ID are required", "", "warning");
      return;
    }
    try {
      if (isEdit) await paymentGatewayConfigurationApi.update(id, form);
      else await paymentGatewayConfigurationApi.create(form);
      Swal.fire("Saved", "", "success").then(() => navigate("/gateway-configurations"));
    } catch (e) {
      Swal.fire(e?.response?.data?.message || "Save failed", "", "error");
    }
  };

  return (
    <div>
      <Header />
      <Menu />
      <div className="content-wrapper">
        <div className="UI-container">
          <div className="UI-header">
            <Breadcrumb breadcrumbs={[{ name: "Home", path: "/superadminmasters" }, { name: "Gateway Configurations", path: "/gateway-configurations" }, { name: isEdit ? "Edit" : "Create", path: null }]} />
          </div>
          <div className="UI-form-card">
            <div className="form-row">
              <fieldset className="col-md-3">
                <div style={{ position: "relative" }}>
                  <SelectField label="Gateway" list={gateways} lableKey="displayName" lableValue="id" value={form.gatewayId} onChange={(e) => set("gatewayId", e.target.value)} required />
                  <button
                    type="button"
                    title="Add Gateway"
                    onClick={goAddGateway}
                    style={{ position: "absolute", right: "8px", top: "30px", width: "22px", height: "22px", borderRadius: "50%", border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", fontSize: "14px", lineHeight: "1", color: "#374151" }}
                  >
                    +
                  </button>
                </div>
                {selectedGateway && (
                  <div className="tw-flex tw-items-center tw-gap-2 tw-pt-2">
                    <GatewayLogo logoUrl={selectedGateway.logoUrl} size={30} />
                    <span className="tw-text-xs tw-text-slate-500">{selectedGateway.displayName || selectedGateway.code}</span>
                  </div>
                )}
              </fieldset>
              <fieldset className="col-md-3">
                <TextfieldComponent type="text" name="tenantId" label="Tenant ID" value={form.tenantId || ""} onChange={(e) => set("tenantId", e.target.value)} required />
              </fieldset>
              {secretFields.map((f) => (
                <fieldset className="col-md-3" key={f.name}>
                  <TextfieldComponent type="text" name={f.name} label={f.label} value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} />
                </fieldset>
              ))}
              <fieldset className="col-md-3">
                <div className="UI-form-group tw-pb-1">
                  <label className="UI-label">Production</label>
                  <div className="tw-flex tw-items-center tw-gap-2 tw-pt-2">
                    <input type="checkbox" className="tw-w-5 tw-h-5 tw-accent-[#fdd60e] tw-cursor-pointer" checked={Boolean(form.production)} onChange={(e) => set("production", e.target.checked)} />
                    <span className="tw-text-sm">{form.production ? "Production" : "Sandbox"}</span>
                  </div>
                </div>
              </fieldset>
              <fieldset className="col-md-3">
                <div className="UI-form-group tw-pb-1">
                  <label className="UI-label">Active</label>
                  <div className="tw-flex tw-items-center tw-gap-2 tw-pt-2">
                    <input type="checkbox" className="tw-w-5 tw-h-5 tw-accent-[#fdd60e] tw-cursor-pointer" checked={Boolean(form.active)} onChange={(e) => set("active", e.target.checked)} />
                    <span className="tw-text-sm">{form.active ? "Yes" : "No"}</span>
                  </div>
                </div>
              </fieldset>
              <fieldset className="col-md-12 tw-flex tw-justify-end">
                <button className="UI-btn UI-btn-primary tw-mr-2" type="button" onClick={submit}>Save</button>
                <Link as="button" className="UI-btn UI-btn-outline col-md-auto col-sm-12" to={{ pathname: "/gateway-configurations" }}>Cancel</Link>
              </fieldset>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
